import {gql} from '@apollo/client';
import {DocumentNode} from 'graphql';
import React from 'react';
import _ from 'underscore';

import isArchived from 'archive/isArchived';
import {assert} from 'Asserts';
import {Permissions} from 'auth/PermissionConstants';
import BenchlingQuery from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {graphQLArrayToREST} from 'graphql-client/declarative-translations/external/translate';
import {AssayRunSchemaSelectorQuery} from 'notebook/integrations/runs/__generated/AssayRunSchemaSelectorQuery';
import {AssayRunSchema} from 'results/assay-schemas/AssaySchemaConstants';
import {BaseEmptyAssayGroup} from 'runs/assay-group/EmptyAssayGroup';
import {getSchemaOptionDataMetadata} from 'schemas/SchemaOwnerUtil';
import FormGroup from 'ui-library/bs/FormGroup';
import Label from 'ui-library/bs/Label';
import SelectDropdown, {closeSelectDropdown} from 'ui-library/dropdowns/SelectDropdown';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import Menu from 'ui-library/menus/Menu';
import SearchableMenu from 'ui-library/menus/SearchableMenu';

const DEFAULT_SHOULD_SHOW_NO_SCHEMA_OPTION = false;
interface AssayRunSchemaSelectorProps {
  assayRunSchemas: Array<AssayRunSchema>;
  selectedAssayRunSchema: AssayRunSchema | null;
  // assayRunSchema may be null if `shouldShowNoSchemaOption` is true.  Otherwise, it should not be null.
  onAssayRunSchemaChange: (assayRunSchema: AssayRunSchema | null) => void;
  shouldShowNoSchemaOption?: boolean;
  showPreview?: boolean;
  requiredPermissions?: Array<keyof Permissions>;
}

export const ASSAY_RUN_SCHEMA_SELECTOR_QUERY: DocumentNode = gql`
  query AssayRunSchemaSelectorQuery @skipAudit @engComponent(name: LAB_AUTOMATION) {
    ...AllAssaySchemasGraphQL
  }
  ${getFragmentWrapper('AllAssaySchemas').fragment}
`;

function QueryingAssayRunSchemaSelector(
  props: Omit<AssayRunSchemaSelectorProps, 'assayRunSchemas'>
): JSX.Element {
  return (
    <AssayRunSchemaSelectorIdInput
      {...props}
      selectedAssayRunSchemaId={props.selectedAssayRunSchema?.id ?? null}
    />
  );
}

interface AssayRunSchemaSelectorIdInputProps extends Omit<
  AssayRunSchemaSelectorProps,
  'selectedAssayRunSchema'
> {
  selectedAssayRunSchemaId: string | null;
}

export function AssayRunSchemaSelectorIdInput(
  props: Omit<AssayRunSchemaSelectorIdInputProps, 'assayRunSchemas'>
): JSX.Element {
  return (
    <BenchlingQuery<AssayRunSchemaSelectorQuery>
      query={ASSAY_RUN_SCHEMA_SELECTOR_QUERY}
      renderLoading={() => <CenteredHorizontalThrobber />}
    >
      {({runSchemas}) => {
        const restRunSchemas: Array<AssayRunSchema> = graphQLArrayToREST('RunSchema', runSchemas);

        const filteredRunSchemas = restRunSchemas
          .filter((schema) => !isArchived(schema))
          .filter(
            (schema) =>
              !props.requiredPermissions ||
              props.requiredPermissions.every((perm) => schema.permissions[perm])
          );

        const selectedAssayRunSchema =
          filteredRunSchemas.find((runSchema) => runSchema.id === props.selectedAssayRunSchemaId) ?? null;

        return (
          <AssayRunSchemaSelector
            selectedAssayRunSchema={selectedAssayRunSchema}
            onAssayRunSchemaChange={props.onAssayRunSchemaChange}
            assayRunSchemas={filteredRunSchemas}
            showPreview={props.showPreview}
            shouldShowNoSchemaOption={props.shouldShowNoSchemaOption}
          />
        );
      }}
    </BenchlingQuery>
  );
}

interface EmptyAssayGroupPreviewProps {
  assayRunSchema: AssayRunSchema;
}

/**
 * Read-only component for an empty run.
 */
export function EmptyAssayGroupPreview(props: EmptyAssayGroupPreviewProps): JSX.Element {
  return (
    <BaseEmptyAssayGroup
      assayRunSchemaInfo={{
        assayRunSchema: props.assayRunSchema,
        versionedName: null,
        versionedFields: null,
      }}
      cid=""
      indentation={0}
      locked={true}
      isLoading={false}
    />
  );
}

class AssayRunSchemaSelector extends React.Component<AssayRunSchemaSelectorProps> {
  static defaultProps = {
    showPreview: true,
  };

  dropdown: SelectDropdown | null = null;

  _handleAssayRunSchemaChange: (value: string | null) => void = (value) => {
    if (value == null) {
      this.props.onAssayRunSchemaChange(null);
      closeSelectDropdown(this.dropdown!);
      return;
    }
    const assayRunSchemaId = value;
    const assayRunSchema = _.findWhere(this.props.assayRunSchemas, {id: assayRunSchemaId});
    assert(assayRunSchema != null, 'Selected assay schema not found', {id: assayRunSchemaId});
    this.props.onAssayRunSchemaChange(assayRunSchema);
    closeSelectDropdown(this.dropdown!);
  };

  override render(): JSX.Element {
    const assayRunSchema = this.props.selectedAssayRunSchema;
    const shouldShowNoSchemaOption =
      this.props.shouldShowNoSchemaOption ?? DEFAULT_SHOULD_SHOW_NO_SCHEMA_OPTION;

    const getMetadata = getSchemaOptionDataMetadata(
      this.props.assayRunSchemas,
      (schema) => schema.organization.name
    );
    const optionDatas = [];
    if (shouldShowNoSchemaOption) {
      optionDatas.push({name: 'No schema', value: null});
      optionDatas.push({name: 'Schemas', isHeader: true});
    }
    optionDatas.push(
      ...this.props.assayRunSchemas.map((schema) => ({
        value: schema.id,
        name: schema.name,
        metadata: getMetadata(schema),
      }))
    );

    return (
      <div>
        <SelectDropdown
          disabled={false}
          displayName={assayRunSchema == null ? 'Select...' : assayRunSchema.name}
          closeOnInsideClick={true}
          closeOnOutsideClick={true}
          menu={
            <SearchableMenu
              onSelect={this._handleAssayRunSchemaChange}
              optionDatas={optionDatas}
              selectedValue={assayRunSchema == null ? null : assayRunSchema.id}
              width={Menu.WIDTHS.FULL}
            />
          }
          ref={(node) => {
            this.dropdown = node;
          }}
        />
        {assayRunSchema != null && this.props.showPreview && (
          <FormGroup className="u-spacing-top">
            <Label>Preview</Label>
            <div className="modal-mediocre-tableWrapper">
              <EmptyAssayGroupPreview assayRunSchema={assayRunSchema} />
            </div>
          </FormGroup>
        )}
      </div>
    );
  }
}

export default QueryingAssayRunSchemaSelector;
